import { ArrowLeftOutlined, ArrowRightOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Form, FormInstance, FormListFieldData, Input, Row, Select, Typography } from "antd";
import React, { useMemo, useState } from "react";
import { TransferFormValues, TransferListingItem } from "../Create";
import { EListingType } from "../../../types/listing.type";
import { UserProperties } from "../../../types/user.type";
import AssignAgentModal, { AssignAgentModalCallback } from "./AssignAgentModal";
import { doFilterItem } from "../helper";
import ListingGridView from "./ListingGridView";
import ListingListView from "./ListingListView";
import { ReactComponent as ListUnordered } from "../../../assets/Icons/ListUnordered.svg";
import { ReactComponent as MoreGrid } from "../../../assets/Icons/MoreGrid.svg";

interface Props {
	user?: UserProperties;
	employeeId?: string;
	officeId?: string;
	form: FormInstance<TransferFormValues>;
	isTransferListing?: boolean;
}
const { Title, Text } = Typography;
const ListingCard: React.FC<Props> = ({ user, form, employeeId, officeId, isTransferListing }) => {
	const [searchMyListing, setSearchMyListing] = useState<string>("");
	const [filterMyListing, setFilterMyListing] = useState<EListingType | "">("");
	const [showManyTransferModal, setShowManyTransferModal] = useState<boolean>(false);
	const [showSingleTransfer, setShowSingleTransfer] = useState<TransferListingItem>();
	const [isMyListingGridView, setIsMyListingGridView] = useState<boolean>(true);

	const [searchTransferListing, setSearchTransferListing] = useState<string>("");
	const [filterTransferListing, setFilterTransferListing] = useState<EListingType | "">("");
	const [isTransferListingGridView, setIsTransferListingGridView] = useState<boolean>(true);

	const generateAllCheckState = (listings?: TransferListingItem[]) => {
		if (listings && listings.length > 0) {
			let checkedList = listings.filter((item) => item.checked);
			if (checkedList.length === 0) {
				return {
					checked: false,
					indeterminate: false,
				};
			}
			if (checkedList.length === listings.length) {
				return {
					checked: true,
					indeterminate: false,
				};
			}
			if (checkedList.length < listings.length) {
				return {
					checked: true,
					indeterminate: true,
				};
			}
		}
		return {
			checked: false,
			indeterminate: false,
		};
	};
	const userListingWatch = Form.useWatch("userListing", form);
	const transferListingWatch = Form.useWatch("transferListing", form);

	// MY LISTING
	const myListingCheckAll = useMemo(() => {
		return generateAllCheckState(userListingWatch);
	}, [userListingWatch]);

	const _handleMyCheckBoxAll = (checked: boolean) => {
		form.setFieldValue(
			"userListing",
			userListingWatch?.map((item) => ({
				...item,
				checked,
			}))
		);
	};

	const mySelectedListing = useMemo(() => userListingWatch?.filter((item) => item.checked), [userListingWatch]);
	const _handleUncheckMySingleListing = (listingId: string) => {
		form.setFieldValue(
			"userListing",
			userListingWatch?.map((item) => ({
				...item,
				checked: item.listingId === listingId ? false : item.checked,
			}))
		);
	};

	const filterMyItem = (field: FormListFieldData) => {
		let item: TransferListingItem = form.getFieldValue(["userListing", field.name]);

		return doFilterItem(item, searchMyListing, filterMyListing);
	};

	const moveToTransferList = (value: AssignAgentModalCallback) => {
		let newTransferListing = value.selectedListing.map((item) => ({
			...item,
			checked: false,
			agent: value.newOwner,
		}));
		let movedUserListingId = newTransferListing.map((item) => item.listingId);
		form.setFieldValue(
			"userListing",
			userListingWatch?.filter((item) => !movedUserListingId.includes(item.listingId))
		);

		let existingTransfer: TransferListingItem[] = transferListingWatch ?? [];
		form.setFieldValue("transferListing", [...existingTransfer, ...newTransferListing]);
	};

	// TRANSFERED LISTING
	const transferListingCheckAll = useMemo(() => {
		return generateAllCheckState(transferListingWatch);
	}, [transferListingWatch]);

	const _handleTransferCheckBoxAll = (checked: boolean) => {
		form.setFieldValue(
			"transferListing",
			transferListingWatch?.map((item) => ({
				...item,
				checked,
			}))
		);
	};

	const filterTransferItem = (field: FormListFieldData) => {
		let item: TransferListingItem = form.getFieldValue(["transferListing", field.name]);

		return doFilterItem(item, searchTransferListing, filterTransferListing);
	};

	const moveToMyList = () => {
		let newMyListing =
			transferListingWatch
				?.filter((item) => item.checked)
				.map((item) => ({
					...item,
					checked: false,
					agent: {
						name: user?.nickName,
						userId: user?.userId,
						employeeId,
						officeId,
					},
				})) ?? [];

		let movedUserListingId = newMyListing.map((item) => item.listingId);
		form.setFieldValue(
			"transferListing",
			transferListingWatch?.filter((item) => !movedUserListingId.includes(item.listingId))
		);

		let existingMyListing = userListingWatch ?? [];
		form.setFieldValue("userListing", [...existingMyListing, ...newMyListing]);
	};

	return (
		<React.Fragment>
			<Card
				bordered={false}
				size="small"
			>
				<div className="transfer-grid">
					<div>
						<Title level={3}>{user?.nickName} Listing</Title>
						<Text type="secondary">List of all listing that belongs to {user?.nickName}</Text>
						<Row
							gutter={[24, 0]}
							style={{ marginTop: 20 }}
							wrap={false}
							align="bottom"
						>
							<Col span={12}>
								<Text>Search</Text>
								<Input
									value={searchMyListing}
									onChange={(e) => setSearchMyListing(e.target.value)}
									placeholder="Search"
									suffix={<SearchOutlined />}
								/>
							</Col>
							<Col flex="auto">
								<Text>Filter Type</Text>
								<Select
									virtual={true}
									value={filterMyListing}
									onChange={setFilterMyListing}
									placeholder="All"
									options={[
										{ value: "", label: "All" },
										{ value: EListingType.PRIMARY, label: "Primary" },
										{ value: EListingType.SECONDARY, label: "Secondary" },
									]}
								/>
							</Col>
							<Col flex="65px">
								<Button
									onClick={() => setIsMyListingGridView(!isMyListingGridView)}
									className="btn-outlined"
									type="default"
									icon={isMyListingGridView ? <MoreGrid /> : <ListUnordered />}
								/>
							</Col>
						</Row>
						<div
							className="ant-transfer-list"
							style={{ width: "100%", marginTop: 20, height: 500 }}
						>
							{isMyListingGridView ? (
								<ListingGridView
									checkAllState={myListingCheckAll}
									fieldName="userListing"
									onShowSingleTransfer={(item) => setShowSingleTransfer(item)}
									filterItem={filterMyItem}
									onCheckAll={_handleMyCheckBoxAll}
								/>
							) : (
								<ListingListView
									checkAllState={myListingCheckAll}
									fieldName="userListing"
									onShowSingleTransfer={(item) => setShowSingleTransfer(item)}
									// filterItem={filterMyItem}
									filtered={{ search: searchMyListing, type: filterMyListing }}
									onCheckAll={_handleMyCheckBoxAll}
								/>
							)}
						</div>
					</div>
					<div className="transfer-listing-separator">
						<Button
							type="primary"
							icon={<ArrowRightOutlined />}
							danger={true}
							style={{ marginBottom: 20 }}
							onClick={() => setShowManyTransferModal(true)}
							disabled={!myListingCheckAll.checked}
						/>
						<Button
							type="primary"
							icon={<ArrowLeftOutlined />}
							disabled={!transferListingCheckAll.checked}
							onClick={moveToMyList}
						/>
					</div>
					<div>
						<Title level={3}>Transferred Listing</Title>
						<Text type="secondary">List listing that that will be transferred to other agent</Text>
						<Row
							gutter={[24, 0]}
							style={{ marginTop: 20 }}
							wrap={false}
							align="bottom"
						>
							<Col span={12}>
								<Text>Search</Text>
								<Input
									value={searchTransferListing}
									onChange={(e) => setSearchTransferListing(e.target.value)}
									placeholder="Search"
									suffix={<SearchOutlined />}
								/>
							</Col>
							<Col flex="auto">
								<Text>Filter Type</Text>
								<Select
									virtual={true}
									value={filterTransferListing}
									onChange={setFilterTransferListing}
									placeholder="All"
									options={[
										{ value: "", label: "All" },
										{ value: EListingType.PRIMARY, label: "Primary" },
										{ value: EListingType.SECONDARY, label: "Secondary" },
									]}
								/>
							</Col>
							<Col flex="65px">
								<Button
									onClick={() => setIsTransferListingGridView(!isTransferListingGridView)}
									className="btn-outlined"
									type="default"
									icon={isTransferListingGridView ? <MoreGrid /> : <ListUnordered />}
								/>
							</Col>
						</Row>
						<div
							className="ant-transfer-list"
							style={{ width: "100%", marginTop: 20, height: 500 }}
						>
							{isTransferListingGridView ? (
								<ListingGridView
									checkAllState={transferListingCheckAll}
									fieldName="transferListing"
									disablePickSingle={true}
									filterItem={filterTransferItem}
									onCheckAll={_handleTransferCheckBoxAll}
								/>
							) : (
								<ListingListView
									checkAllState={transferListingCheckAll}
									fieldName="transferListing"
									disablePickSingle={true}
									// filterItem={filterTransferItem}
									filtered={{ search: searchTransferListing, type: filterTransferListing }}
									onCheckAll={_handleTransferCheckBoxAll}
								/>
							)}
						</div>
					</div>
				</div>
			</Card>
			<AssignAgentModal
				isTransferListing={isTransferListing}
				open={showManyTransferModal}
				selectedListing={mySelectedListing}
				onClose={() => setShowManyTransferModal(false)}
				onUnselect={_handleUncheckMySingleListing}
				onSubmit={moveToTransferList}
				officeIdProps={isTransferListing && officeId ? officeId : undefined}
			/>
			<AssignAgentModal
				isTransferListing={isTransferListing}
				isSingle={true}
				open={showSingleTransfer !== undefined}
				selectedListing={showSingleTransfer ? [showSingleTransfer] : undefined}
				onClose={() => setShowSingleTransfer(undefined)}
				onSubmit={moveToTransferList}
				officeIdProps={isTransferListing && officeId ? officeId : undefined}
			/>
		</React.Fragment>
	);
};
export default ListingCard;
